import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { EmptyState, ErrorState, Loading } from "@lopie/kernel-ui";
import { api } from "../lib/projects-api.ts";

const TABS = ["active", "maintenance", "internal", "archived"];
const LABEL = { active: "Active", maintenance: "Maintenance", internal: "Internal", archived: "Archived" };

// A project belongs to exactly one tab. Internal wins over maintenance: a
// project that is both stays with the rest of the internal work rather than
// dropping into Maintenance. On hold is a badge, not a tab — a paused
// project is still wherever it already was.
const bucketOf = (p) => {
  if (p.archived) return "archived";
  if (p.internal) return "internal";
  if (p.maintenanceMode) return "maintenance";
  return "active";
};

/** The list of projects, by tab, and how a new one is made. Opening one is a
 *  click on its card — the board is where the work is. */
export default function Projects({ onOpen }) {
  const [projects, setProjects] = useState(null);
  const [failure, setFailure] = useState(null);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("lopie:projects-tab") || "active"; }
    catch { return "active"; }
  });

  const load = () => {
    setFailure(null);
    // archived=1 is not "only archived" — the server's rule is "include
    // archived too", so one call returns every project and the tabs below
    // are a client-side bucket of that one list, the same as the monolith.
    api.projects({ archived: true }).then((r) => setProjects(r.items)).catch(setFailure);
  };
  useEffect(load, []);

  useEffect(() => {
    try { localStorage.setItem("lopie:projects-tab", tab); } catch { /* private mode */ }
  }, [tab]);

  if (failure) return <ErrorState failure={failure} onRetry={load} />;
  if (!projects) return <Loading label="Reading your projects" />;

  const counts = Object.fromEntries(TABS.map((t) => [t, 0]));
  for (const p of projects) counts[bucketOf(p)] += 1;
  const shown = projects.filter((p) => bucketOf(p) === tab);

  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="h5" sx={{ flex: 1 }}>Your projects</Typography>
        <Button variant="contained" onClick={() => setCreating(true)}>New project</Button>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        {TABS.map((t) => (
          <Tab key={t} value={t} label={`${LABEL[t]} (${counts[t]})`} />
        ))}
      </Tabs>

      {!shown.length ? (
        <EmptyState
          title={`No ${LABEL[tab].toLowerCase()} projects`}
          detail={tab === "active"
            ? "A project holds a board of work. Make one and add columns and cards."
            : `A project shows up here once it is marked ${LABEL[tab].toLowerCase()} in its settings.`}
        />
      ) : null}

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" } }}>
        {shown.map((p) => (
          <Paper key={p.id} variant="outlined" component="button" type="button"
            onClick={() => onOpen(p)}
            sx={{ p: 2, cursor: "pointer", textAlign: "left", bgcolor: "background.paper" }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: p.color || "primary.main" }} />
                <Typography variant="h6" sx={{ flex: 1 }}>{p.name}</Typography>
                {p.onHold ? (
                  <Tooltip title="Paused"><Chip size="small" color="warning" variant="outlined" label="paused" /></Tooltip>
                ) : null}
                <Chip size="small" variant="outlined" label={p.key} sx={{ fontFamily: "monospace" }} />
              </Stack>
              {p.description ? (
                <Typography variant="body2" color="text.secondary">{p.description}</Typography>
              ) : null}
            </Stack>
          </Paper>
        ))}
      </Box>

      <NewProject open={creating} onClose={() => setCreating(false)}
        onCreated={(p) => { setCreating(false); setProjects([...projects, p]); onOpen(p); }} />
    </Stack>
  );
}

function NewProject({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const project = await api.createProject({ name, key, description });
      setName(""); setKey(""); setDescription("");
      onCreated(project);
    } catch (e) {
      setError(e.body?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New project</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus fullWidth />
          <TextField label="Key" value={key} onChange={(e) => setKey(e.target.value.toUpperCase())}
            helperText="Short prefix for task numbers, e.g. VCPO. 2-10 letters or digits."
            fullWidth slotProps={{ htmlInput: { maxLength: 10 } }} />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)}
            multiline minRows={2} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !name.trim() || !key.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
